import {
  DEFAULT_TRIAL_HOME,
  mergeTrialHomeWithDefaults,
  TRIAL_INTENT_LABELS,
  TRIAL_TOUR_MANIFESTS,
  TrialHomeContent,
} from '@chiroup/core/types/TrialHome.type';

import { Icon } from '@chiroup/components/Icon';
import { Icons } from '@chiroup/components/icons';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import appDataService from '../../services/app-data.service';
import trialAnalyticsService, {
  TrialAnalyticsCard,
  TrialAnalyticsChecklistRow,
  TrialAnalyticsIntentRow,
  TrialAnalyticsResponse,
  TrialAnalyticsTotals,
  TrialAnalyticsTour,
} from '../../services/trialAnalytics.service';
import Header from '../layout/Header';

/**
 * Aggregate trial-experience analytics. Tour funnel is the headline insight
 * (unique to this dashboard; the home page builder has no multi-step flows).
 *
 * Phase-2 scope: aggregate only. No per-clinic drill-down, no date pickers
 * other than a few rolling-window presets. Add if/when product asks.
 */

const WINDOW_OPTIONS = [
  { label: '7 days', value: 7 },
  { label: '30 days', value: 30 },
  { label: '90 days', value: 90 },
  { label: '365 days', value: 365 },
];

// Stable rowIds emitted by the trial checklist widget. Kept in sync with
// `computeItems` in TrialChecklist.tsx — adding a new row there means
// adding a label here so the analytics view doesn't show a raw slug.
// Display labels for stored `trialIntent` values come from the shared
// TRIAL_INTENT_LABELS (also used for the Salesforce Platform_Event payload).
const INTENT_LABELS: Record<string, string> = TRIAL_INTENT_LABELS;

const CHECKLIST_ROW_LABELS: Record<string, string> = {
  'setup-account': 'Set up your account',
  'create-first-report': 'Create your first Condition Report',
  'add-clinic-info': 'Add clinic info to personalize reports',
  'create-patient': 'Create a patient',
  'create-more-reports': 'Create 5 reports',
  'explore-protocols': 'Explore Clinical Protocols',
};

const TrialAnalyticsPage = () => {
  const [windowDays, setWindowDays] = useState(30);
  const [data, setData] = useState<TrialAnalyticsResponse | null>(null);
  const [trialHome, setTrialHome] = useState<TrialHomeContent>(DEFAULT_TRIAL_HOME);
  const [loading, setLoading] = useState(true);

  // Resolve card titles from the live trialHome payload so the cards section
  // reads naturally ("Condition Reports", not "condition-reports"). Falls
  // back to the slug if a card was renamed / deleted after the events fired.
  useEffect(() => {
    appDataService
      .get('trialHome')
      .then(res => {
        if (res) setTrialHome(mergeTrialHomeWithDefaults(res));
      })
      .catch(() => {
        // No live payload yet — defaults stand.
      });
  }, []);

  useEffect(() => {
    let destroyed = false;
    setLoading(true);
    trialAnalyticsService
      .get(windowDays)
      .then(res => {
        if (!destroyed) setData(res);
      })
      .finally(() => {
        if (!destroyed) setLoading(false);
      });

    return () => {
      destroyed = true;
    };
  }, [windowDays]);

  const cardLabels = useMemo(() => {
    const map: Record<string, string> = {};
    for (const card of trialHome.cards) map[card.id] = card.title || card.id;

    return map;
  }, [trialHome.cards]);

  return (
    <>
      <Header
        title={
          <div className="flex items-center gap-3">
            <Link
              to="/home-page/trial-experience"
              className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-800 transition-colors"
            >
              <Icon icon={Icons.arrowLeft} className="h-3.5 w-3.5" />
              Back to editor
            </Link>
            <span className="h-4 w-px bg-gray-200" aria-hidden="true" />
            <h1 className="text-base font-semibold text-gray-900">Trial Analytics</h1>
          </div>
        }
        rightSide={
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500">Window:</span>
            <div className="inline-flex rounded-md border border-gray-200 overflow-hidden">
              {WINDOW_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setWindowDays(opt.value)}
                  className={[
                    'px-3 py-1.5 text-xs font-medium transition-colors',
                    windowDays === opt.value
                      ? 'bg-primary-600 text-white'
                      : 'bg-white text-gray-600 hover:bg-gray-50',
                  ].join(' ')}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        }
      />
      <div className="p-4 space-y-6">
        <TotalsStrip totals={data?.totals} loading={loading} />
        <IntentSection rows={data?.intent ?? []} loading={loading} />
        <ToursSection tours={data?.tours ?? []} loading={loading} />
        <CardsSection cards={data?.cards ?? []} cardLabels={cardLabels} loading={loading} />
        <ChecklistSection rows={data?.checklist ?? []} loading={loading} />
      </div>
    </>
  );
};

// ---------------------------------------------------------------------------
// Totals strip
// ---------------------------------------------------------------------------

const TotalsStrip: React.FC<{ totals?: TrialAnalyticsTotals; loading: boolean }> = ({
  totals,
  loading,
}) => {
  // Display the unique-clinic count by default; show the raw event count
  // as a small sublabel so admins who care about volume (replays / remounts)
  // can see both. Completion rate is computed from `clinics` to match the
  // per-tour funnel's units.
  const starts = totals?.tourStarts.clinics ?? 0;
  const completions = totals?.tourCompletions.clinics ?? 0;
  const completionRate = starts > 0 ? `${Math.round((completions / starts) * 100)}%` : '—';

  const cells: Array<{ label: string; value: string | number; sub?: string }> = [
    {
      label: 'Home — clinics',
      value: totals?.homeViews.clinics ?? 0,
      sub: `${totals?.homeViews.events ?? 0} views`,
    },
    { label: 'Tour starts', value: starts, sub: `${totals?.tourStarts.events ?? 0} events` },
    {
      label: 'Tour completions',
      value: completions,
      sub: `${totals?.tourCompletions.events ?? 0} events`,
    },
    { label: 'Completion rate', value: completionRate },
    {
      label: 'Personalize opens',
      value: totals?.personalizeOpens.clinics ?? 0,
      sub: `${totals?.personalizeOpens.events ?? 0} events`,
    },
    {
      label: 'Personalize saves',
      value: totals?.personalizeSaves.clinics ?? 0,
      sub: `${totals?.personalizeSaves.events ?? 0} events`,
    },
  ];

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
      {cells.map(cell => (
        <div
          key={cell.label}
          className="bg-white border border-gray-200 rounded-lg p-3 flex flex-col gap-0.5"
        >
          <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">
            {cell.label}
          </div>
          <div className="text-xl font-semibold text-gray-900 tabular-nums leading-tight">
            {loading ? <span className="text-gray-300">…</span> : cell.value}
          </div>
          {cell.sub && !loading && (
            <div className="text-[10px] text-gray-400 tabular-nums">{cell.sub}</div>
          )}
        </div>
      ))}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Tours — funnel per tour
// ---------------------------------------------------------------------------

const ToursSection: React.FC<{ tours: TrialAnalyticsTour[]; loading: boolean }> = ({
  tours,
  loading,
}) => {
  // Resolve tour metadata (label + canonical step order) from the manifest
  // so the funnel renders even for steps that had 0 distinct clinics in the
  // window. Without this, the funnel would only show "steps the user reached"
  // and you couldn't see drop-off at unseen steps.
  const tourMeta = useMemo(() => {
    const map = new Map(TRIAL_TOUR_MANIFESTS.map(t => [t.id, t]));

    return map;
  }, []);

  if (loading) return <SectionLoader title="Tour funnels" />;

  if (tours.length === 0) {
    return (
      <Section title="Tour funnels">
        <EmptyState message="No tour activity in this window yet." />
      </Section>
    );
  }

  return (
    <Section title="Tour funnels">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {tours.map(tour => {
          const meta = tourMeta.get(tour.tourId);
          const stepsByIndex = new Map(tour.steps.map(s => [s.stepIndex, s]));
          const canonicalSteps = meta
            ? meta.steps.map((manifestStep, i) => ({
                stepIndex: i,
                stepKey: manifestStep.stepKey,
                stepTitle: manifestStep.title || manifestStep.stepKey,
                distinctClinics: stepsByIndex.get(i)?.distinctClinics ?? 0,
              }))
            : tour.steps.map(s => ({
                stepIndex: s.stepIndex,
                stepKey: s.stepKey,
                stepTitle: s.stepKey || `Step ${s.stepIndex + 1}`,
                distinctClinics: s.distinctClinics,
              }));

          const maxDistinct = Math.max(1, ...canonicalSteps.map(s => s.distinctClinics));
          // All values here are unique-clinic counts so baselines and
          // percentages stay in the same units. Baseline = max of tour-started
          // and step-0 reach: normally equal, but a rare buffered-event loss
          // could leave step-0 slightly below started.
          const startedFromSteps = canonicalSteps[0]?.distinctClinics ?? 0;
          const baseline = Math.max(tour.started, startedFromSteps, 1);
          const completionRate =
            tour.started > 0 ? Math.round((tour.completed / tour.started) * 100) : 0;

          return (
            <div
              key={tour.tourId}
              className="bg-white border border-gray-200 rounded-lg p-4 flex flex-col gap-3"
            >
              <div className="flex items-baseline justify-between gap-3">
                <div>
                  <div className="text-sm font-semibold text-gray-900">
                    {meta?.label ?? tour.tourId}
                  </div>
                  <div className="text-[11px] text-gray-500">{tour.tourId}</div>
                </div>
                <div className="text-right">
                  <div className="text-xs text-gray-500">Completion</div>
                  <div className="text-base font-semibold text-primary-700 tabular-nums">
                    {completionRate}%
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-2 text-center">
                <FunnelStat label="Started" value={tour.started} tone="primary" />
                <FunnelStat label="Completed" value={tour.completed} tone="success" />
                <FunnelStat label="Skipped" value={tour.skipped} tone="muted" />
              </div>

              <div className="flex flex-col gap-1.5 pt-1">
                {canonicalSteps.map(step => {
                  const pct =
                    baseline > 0 ? Math.round((step.distinctClinics / baseline) * 100) : 0;
                  // Bar width relative to the leader so drop-offs are visible
                  // even when started count itself is small.
                  const widthPct = Math.max(
                    2,
                    Math.round((step.distinctClinics / maxDistinct) * 100)
                  );

                  return (
                    <div
                      key={step.stepIndex}
                      className="grid grid-cols-[24px_1fr_auto] items-center gap-2"
                    >
                      <span className="text-[10px] font-mono text-gray-400 text-right">
                        {step.stepIndex + 1}
                      </span>
                      <div className="relative h-6 rounded-md bg-gray-100 overflow-hidden">
                        <div
                          className="absolute inset-y-0 left-0 bg-primary-100"
                          style={{ width: `${widthPct}%` }}
                        />
                        <div className="absolute inset-0 flex items-center px-2 text-[11px] font-medium text-gray-700 truncate">
                          {step.stepTitle}
                        </div>
                      </div>
                      <span className="text-[11px] text-gray-600 tabular-nums whitespace-nowrap">
                        {step.distinctClinics}
                        <span className="text-gray-400"> · {pct}%</span>
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </Section>
  );
};

const FunnelStat: React.FC<{
  label: string;
  value: number;
  tone: 'primary' | 'success' | 'muted';
}> = ({ label, value, tone }) => {
  const toneClass =
    tone === 'success'
      ? 'text-green-700 bg-green-50'
      : tone === 'muted'
        ? 'text-gray-600 bg-gray-50'
        : 'text-primary-700 bg-primary-50';

  return (
    <div className={['rounded-md p-2', toneClass].join(' ')}>
      <div className="text-[10px] font-semibold uppercase tracking-wider opacity-80">{label}</div>
      <div className="text-base font-semibold tabular-nums">{value}</div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Cards
// ---------------------------------------------------------------------------

const CardsSection: React.FC<{
  cards: TrialAnalyticsCard[];
  cardLabels: Record<string, string>;
  loading: boolean;
}> = ({ cards, cardLabels, loading }) => {
  if (loading) return <SectionLoader title="Card engagement" />;
  if (cards.length === 0) {
    return (
      <Section title="Card engagement">
        <EmptyState message="No card clicks in this window yet." />
      </Section>
    );
  }

  const maxClicks = Math.max(1, ...cards.map(c => c.clicks));

  return (
    <Section title="Card engagement">
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <RowHeader columns={['Card', 'Clicks', 'Distinct clinics']} />
        {cards.map(card => (
          <Row
            key={card.cardId ?? '__null__'}
            label={cardLabels[card.cardId ?? ''] || card.cardId || 'Unknown card'}
            sublabel={card.cardId ?? undefined}
            bar={card.clicks / maxClicks}
            values={[card.clicks, card.distinctClinics]}
          />
        ))}
      </div>
    </Section>
  );
};

// ---------------------------------------------------------------------------
// Checklist
// ---------------------------------------------------------------------------

const IntentSection: React.FC<{
  rows: TrialAnalyticsIntentRow[];
  loading: boolean;
}> = ({ rows, loading }) => {
  if (loading) return <SectionLoader title="Trial intent" />;
  if (rows.length === 0) {
    return (
      <Section title="Trial intent">
        <EmptyState message="No intent answers in this window yet." />
      </Section>
    );
  }

  const maxUsers = Math.max(1, ...rows.map(r => r.users));

  return (
    <Section title="Trial intent">
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <RowHeader columns={['Answer', 'Users', 'Distinct clinics']} />
        {rows.map(row => (
          <Row
            key={row.intent ?? '__null__'}
            label={INTENT_LABELS[row.intent ?? ''] || row.intent || 'Unknown'}
            sublabel={row.intent ?? undefined}
            bar={row.users / maxUsers}
            values={[row.users, row.distinctClinics]}
          />
        ))}
      </div>
    </Section>
  );
};

const ChecklistSection: React.FC<{
  rows: TrialAnalyticsChecklistRow[];
  loading: boolean;
}> = ({ rows, loading }) => {
  if (loading) return <SectionLoader title="Checklist row clicks" />;
  if (rows.length === 0) {
    return (
      <Section title="Checklist row clicks">
        <EmptyState message="No checklist clicks in this window yet." />
      </Section>
    );
  }

  const maxClicks = Math.max(1, ...rows.map(r => r.clicks));

  return (
    <Section title="Checklist row clicks">
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <RowHeader columns={['Row', 'Clicks', 'Distinct clinics']} />
        {rows.map(row => (
          <Row
            key={row.rowId ?? '__null__'}
            label={CHECKLIST_ROW_LABELS[row.rowId ?? ''] || row.rowId || 'Unknown row'}
            sublabel={row.rowId ?? undefined}
            bar={row.clicks / maxClicks}
            values={[row.clicks, row.distinctClinics]}
          />
        ))}
      </div>
    </Section>
  );
};

// ---------------------------------------------------------------------------
// Shared primitives
// ---------------------------------------------------------------------------

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <section className="flex flex-col gap-2">
    <h2 className="text-xs font-semibold uppercase tracking-wider text-gray-500">{title}</h2>
    {children}
  </section>
);

const SectionLoader: React.FC<{ title: string }> = ({ title }) => (
  <Section title={title}>
    <div className="bg-white border border-gray-200 rounded-lg p-6 text-sm text-gray-400 italic">
      Loading…
    </div>
  </Section>
);

const EmptyState: React.FC<{ message: string }> = ({ message }) => (
  <div className="bg-white border border-gray-200 rounded-lg p-6 text-sm text-gray-400 italic">
    {message}
  </div>
);

const RowHeader: React.FC<{ columns: string[] }> = ({ columns }) => (
  <div className="grid grid-cols-[1fr_auto_auto] gap-3 px-4 py-2 border-b border-gray-100 bg-gray-50">
    {columns.map((c, i) => (
      <div
        key={c}
        className={[
          'text-[10px] font-semibold uppercase tracking-wider text-gray-500',
          i === 0 ? 'text-left' : 'text-right tabular-nums',
        ].join(' ')}
      >
        {c}
      </div>
    ))}
  </div>
);

const Row: React.FC<{
  label: string;
  sublabel?: string;
  bar: number;
  values: number[];
}> = ({ label, sublabel, bar, values }) => (
  <div className="grid grid-cols-[1fr_auto_auto] gap-3 px-4 py-2.5 border-b border-gray-50 last:border-b-0 items-center">
    <div className="min-w-0">
      <div className="text-sm text-gray-900 truncate">{label}</div>
      {sublabel && <div className="text-[11px] text-gray-400 truncate">{sublabel}</div>}
      <div className="mt-1 h-1 bg-gray-100 rounded overflow-hidden">
        <div
          className="h-full bg-primary-300"
          style={{ width: `${Math.max(2, Math.round(bar * 100))}%` }}
        />
      </div>
    </div>
    {values.map((v, i) => (
      <div key={i} className="text-sm text-gray-700 tabular-nums whitespace-nowrap">
        {v}
      </div>
    ))}
  </div>
);

export default TrialAnalyticsPage;
